import React from "react";
import { useNavigate } from "react-router-dom";
import { Partner, partners } from "../../../../amorg-common";
import { changeUserAccess, changeUserPartner, changeUserPassword } from "../../../../api/adminClientApi";
import { Button } from "../../../../designSystem/button/Button";
import { Flex } from "../../../../designSystem/compositeComponents/FlexHelpers";
import { CheckboxField } from "../../../../designSystem/formField/CheckboxField";
import { SelectField } from "../../../../designSystem/formField/SelectField";
import { StaticTextField } from "../../../../designSystem/formField/StaticTextField";
import { TextField } from "../../../../designSystem/formField/text/TextField";
import { MessageCard } from "../../../../designSystem/MessageCard";
import { Modal } from "../../../../designSystem/Modal";


type EditUserModalProps = {
    show: boolean,
    user: { id: string, email: string, enabled: boolean, partner: Partner | null; } | null,
    onUpdated: () => void,
    onClose: () => void;
};
export function EditUserModal (props: EditUserModalProps): JSX.Element
{
    const navigate = useNavigate();

    const [password, setPassword] = React.useState('');
    const [message, setMessage] = React.useState('');

    React.useEffect(() =>
    {
        const runAsync = async () =>
        {
            setMessage('');
            setPassword('');
        };
        runAsync();
    }, [props.show]);

    async function handlePasswordSave ()
    {
        if(!props.user)
            return;

        const result = await changeUserPassword(props.user.id, password, () => navigate(`/amorgy/login`));

        if (result === true)
        {
            setMessage('Adgangskoden er ændret!');
            props.onUpdated();
        }
        else
            setMessage('Kunne ikke gennemføre ændringen');
    }

    async function handleEnabledSave (enabled: boolean)
    {
        if(!props.user)
            return;

        const result = await changeUserAccess(props.user.id, enabled, () => navigate(`/amorgy/login`));

        if (result === true)
        {
            setMessage('Adgang ændret!');
            props.onUpdated();
        }
        else
            setMessage('Kunne ikke gennemføre ændringen');
    }

    async function handlePartnerSave (value: string)
    {
        if(!props.user)
            return;

        const partner: Partner | null = partners.find(p => p === value) ? value as Partner : null;
        const result = await changeUserPartner(props.user.id, partner, () => navigate(`/amorgy/login`));

        if (result === true)
        {
            setMessage('Partner ændret!');
            props.onUpdated();
        }
        else
            setMessage('Kunne ikke gennemføre ændringen');
    }

    return (
        <Modal
            title={"Rediger bruger"}
            content={
                <Flex gap={24} flexDirection="column">
                    {message &&
                        <MessageCard variant='warning'>
                            {message}
                        </MessageCard>
                    }
                    <UserEditor
                        email={props.user?.email || ''}
                        password={password}
                        enabled={props.user?.enabled || false}
                        partner={props.user?.partner || null}
                        onPasswordChange={setPassword}
                        onPasswordSubmitChange={handlePasswordSave}
                        onEnabledChange={handleEnabledSave}
                        onPartnerChange={handlePartnerSave}
                        onClose={props.onClose}
                    />

                </Flex>
            }
            showModal={props.show}
            onCancel={props.onClose} />
    );
}

type UserEditorProps = {
    email: string,
    password: string,
    enabled: boolean,
    partner: Partner | null,
    onPasswordChange: (newValue: string) => void,
    onPasswordSubmitChange: () => void,
    onEnabledChange: (enabled: boolean) => void,
    onPartnerChange: (partner: string) => void,
    onClose: () => void;
};
function UserEditor (props: UserEditorProps): JSX.Element
{
    return (
        <Flex flexDirection="column" gap={16}>
            <Flex flexDirection="row" gap={16}>
                <StaticTextField
                    label='E-mail'
                    staticText={props.email}
                />
                <CheckboxField
                    text=""
                    value="enabled"
                    checked={props.enabled}
                    onChange={(_, checked) => props.onEnabledChange(checked)}
                    label="Adgang"
                />
            </Flex>
            <SelectField
                selectedValue={props.partner || ''}
                options={[
                    { value: '', text: 'ingen partner' },
                    ...partners.map((partner) => ({ value: partner, text: partner }))
                ]}
                onChange={props.onPartnerChange}
                label="Partner"
            />
            <TextField
                label='Ny adgangskode'
                type='password'
                value={props.password}
                validator={() => ({ valid: true })}
                onChange={props.onPasswordChange}
            />
            <Flex justifyContent="space-between">
                <Button variant="text" icon="actions_close" text='Luk' onClick={props.onClose} />
                <Button variant='filled' text="Skift adgangskode" onClick={props.onPasswordSubmitChange} />
            </Flex>

        </Flex>
    );
}